/**
 * Theme for STWC UK site
 */
import type { Theme } from '@emotion/react';
import { buildTheme, globals } from 'common-ui';
import merge from 'lodash/merge';
import { icons } from 'common-font-icons';
import type { CommonThemeVars, ThemeCreateFunction } from 'tsconfig/types.d';
import {
  buildThemeMixinsLW,
  buildThemeWidgetStylesLW,
  buildThemeVarsLW,
  buildCoreWidgets,
  buildProductCardWidgets,
  buildProductWidgets,
  buildSearchWidgets,
  buildMiniCartWidgets,
  buildCartWidgets,
  buildAccountWidgets,
} from 'dw-uk-law/theme-foundation';
import { playfairDisplayFont, ptSansFont, robotoFont } from '../common/nextFont';

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export const buildThemeVarsSTWC = (vars: Pick<Theme, CommonThemeVars>): ReturnType<typeof buildThemeVarsLW> => {
  //primary
  const stwcBurgundy = '#7A0040'; //color1
  const stwcMing = '#3A7A79'; //color2
  const stwcCream = '#F6F5F2'; //color3
  const stwcLightMing = '#EBF2F2'; //color4
  //secondary
  const stwcPurple = '#1A7C7B'; //color4
  const stwcLightGreen = '#98CAA6'; //color5
  const stwcBlue = '#005C7C'; //color6
  const stwcOrange = '#F18263'; //color7
  const stwcDuckEgg = '#A8BBB7'; //color8
  const stwcSand = '#D7B796'; //color9
  const stwcMushroom = '#786C5E'; //color10
  const stwcDarkMing = '#003538'; //color10
  const stwcBodyColor = '#222222';
  const stwcSvgCheckboxBg = '%233A7A79';

  //CTA colors
  const stwcCTAMingGreen = stwcMing;
  const stwcCTARolloverMing = stwcDarkMing;
  const stwcCTABurgundy = stwcBurgundy;
  const stwcCTARolloverPink = '#6F1D3E';

  const playfairDisplay = playfairDisplayFont.style.fontFamily;
  const PTSans = ptSansFont.style.fontFamily;
  const roboto = robotoFont.style.fontFamily;

  const lwThemeVars = buildThemeVarsLW(vars);

  return merge({}, lwThemeVars, {
    meta: {
      name: 'STWC UK',
    },
    icons,
    assets: {
      headerLogoImgUrl: `${globals.assetsPath}/next/images/brand-logo.svg`,
      headerLogoMobileImgUrl: `${globals.assetsPath}/next/images/brand-logo-mobile.svg`,
      footerLogoImgUrl: `${globals.assetsPath}/next/images/brand-logo-dark.svg`,
    },
    colors: {
      primary1: stwcMing,
      primary2: stwcMing,
      primary3: stwcCream,
      primary5: stwcLightMing,
      secondary1: stwcPurple,
      secondary2: stwcLightGreen,
      secondary3: stwcBlue,
      secondary4: stwcOrange,
      secondary5: stwcDuckEgg,
      secondary6: stwcSand,
      secondary7: stwcMushroom,
      secondary8: stwcBodyColor,

      //specific colour definitions
      btnsPrimaryBg: stwcCTAMingGreen,
      btnsPrimaryColor: vars.colors.white,
      btnsPrimaryHoverBg: stwcCTARolloverMing,
      btnsPrimaryHoverColor: vars.colors.white,
      btnsSecondaryBg: vars.colors.transparent,
      btnsSecondaryColor: vars.colors.black,
      btnsSecondaryHoverBg: stwcCTARolloverMing,
      btnsSecondaryHoverColor: vars.colors.white,
      btnsTertiaryBg: vars.colors.transparent,
      btnsTertiaryColor: vars.colors.black,
      btnsTertiaryBorder: vars.colors.transparent,
      btnsTertiaryHoverBg: vars.colors.transparent,
      btnsTertiaryHoverColor: vars.colors.black,
      svgCheckboxBg: stwcSvgCheckboxBg,
      savingsTextColor: stwcMing,
      textPrimaryBg: stwcMing,
      bgScarcityColor: stwcCTARolloverPink,
      textUnlimitedBg: vars.colors.black,

      linkPrimaryColor: vars.colors.black,
      linkSecondaryColor: stwcCTAMingGreen,
      linkTertiaryColor: stwcCTABurgundy,

      headerCartBtn: stwcCTAMingGreen,
      headerCartBtnSmall: stwcCTAMingGreen,
      headerSignInBtnSmall: stwcCTABurgundy,

      footerPrimaryBg: stwcBurgundy,
      footerGuidelinesFillColor: stwcBurgundy,
      footerIconFillColor: vars.colors.white,
      footerdrinkaware: vars.colors.white,

      bgColors: {
        STWCBurgundy: stwcBurgundy,
        STWCTeal: stwcMing,
        STWCCream: stwcCream,
        STWCPurple: stwcPurple,
        STWCLightGreen: stwcLightGreen,
        STWCBlue: stwcBlue,
        STWCOrange: stwcOrange,
        STWCDuckEgg: stwcDuckEgg,
        STWCSand: stwcSand,
        STWCMushroom: stwcMushroom,
      },
    },
    fonts: {
      sizeH1: '30px',
      sizeH2: '25px',
      sizeH3: '20px',
      familyPrimary: playfairDisplay,
      familySecondary: roboto,
      familyTertiary: PTSans,
      headerMainNavTitle: playfairDisplay,
      headerMainNavSubTitle: PTSans,
      weightLight: '300',
      weightNormal: '400',
      weightMedium: '500',
      weightMediumPlus: '600',
      weightBold: '700',
    },
  });
};

export const buildThemeMixinsSTWC = (
  vars: Omit<Theme, 'mixins' | 'widgets'>,
): ReturnType<typeof buildThemeMixinsLW> => {
  const themeStyles = buildThemeMixinsLW(vars);

  return merge({}, themeStyles, {
    hxl: `
      font-family: ${vars.fonts.familyTertiary};
      font-size: ${vars.fonts.fontSize44};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight100};
      letter-spacing: ${vars.fonts.fontLetterSpacing3};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize60};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight100};
      }
    `,
    h1: `
      font-family: ${vars.fonts.familyTertiary};
      font-size: ${vars.fonts.fontSize34};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight105};
      letter-spacing: ${vars.fonts.fontLetterSpacing3};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize48};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight105};
      }
    `,
    h2: `
      font-family: ${vars.fonts.familyTertiary};
      font-size: ${vars.fonts.fontSize30};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight105};
      letter-spacing: ${vars.fonts.fontLetterSpacing4};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize34};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight105};
      }
    `,
    h3: `
      font-family: ${vars.fonts.familyTertiary};
      font-size: ${vars.fonts.fontSize26};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight110};
      letter-spacing: ${vars.fonts.fontLetterSpacing4};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize30};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight110};
      }
    `,
    h4: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize26};
      font-weight: ${vars.fonts.weightMedium};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize30};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
    h5: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize24};
      font-weight: ${vars.fonts.weightMedium};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize26};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
    h6: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize20};
      font-weight: ${vars.fonts.weightMedium};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize22};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
    h7: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize16};
      font-weight: ${vars.fonts.weightMedium};
      line-height: ${vars.fonts.fontLineHeight130};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize18};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight130};
      }
    `,
    h8: `
      font-family: ${vars.fonts.familyTertiary};
      font-size: ${vars.fonts.fontSize16};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing3};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize18};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    eyebrowRegular: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize12};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing7};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize14};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    eyebrowSmall: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize10};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing7};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize12};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    utility: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize10};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing9};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize12};
        letter-spacing: ${vars.fonts.fontLetterSpacing8};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    quote: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize20};
      font-weight: ${vars.fonts.weightMedium};
      font-style: ${vars.fonts.styleItalic};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing4};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize26};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
    body1: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize16};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    body2: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize14};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight130};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight130};
      }
    `,
    bodySmall: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize12};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight130};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight130};
      }
    `,
    caption: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize12};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
  });
};

type STWCWidgetOverrides = DeepPartial<ReturnType<typeof buildThemeWidgetStylesLW>> & {
  Membership?: { membershipContainer?: string; wrapper?: string; Sidebar?: string; pageWrapper?: string };
};

const buildThemeWidgetStylesSTWCFromBase = (
  vars: Omit<Theme, 'widgets'>,
  themeStyles: DeepPartial<ReturnType<typeof buildThemeWidgetStylesLW>>,
): ReturnType<typeof buildThemeWidgetStylesLW> => {
  const overrides: STWCWidgetOverrides = {
    Footer: {
      default: `
      ${themeStyles.Footer?.default ?? ''}
      .footer-bottom-section {
        background: ${vars.colors.footerPrimaryBg};
        box-shadow: 0 0 0 100vmax ${vars.colors.footerPrimaryBg};
        clip-path: inset(0 -100vmax);
        color: ${vars.colors.white};
        .wrapper .brand img {
          height: 54px !important;
        }
      }
      .social-connections {
        background: ${vars.colors.footerPrimaryBg};
        box-shadow: 0 0 0 100vmax ${vars.colors.footerPrimaryBg};
        clip-path: inset(0 -100vmax);
        .wrapper {
          .social-connection-heading p, i {
            color: ${vars.colors.white};
          }
        }
    }
    `,
      navItems: `
      ${themeStyles.Footer?.navItems ?? ''}
    `,
    },
  };

  if (themeStyles.Search) {
    overrides.Search = {
      hitWrapper: `
      ${themeStyles.Search?.hitWrapper ?? ''}
      .image-container {
        .offer{
          svg path:first-of-type{
            fill:#CB5442!important;
          }
        }
      }
      &.grid-view .ais-Hits-list .ais-Hits-item 
      .addToCart-container .plp-offer-info 
        .member-pricing-container {
          + .price-per-litre, .price-per-litre {
            color: ${vars.colors.black};
          }
        }
      `,
      wrapper: `
      ${themeStyles.Search?.wrapper ?? ''}
      .filter-header {
        p {
          font-weight: ${vars.fonts.weightMedium};
        }
      }
      `,
    };
  }

  if (themeStyles.Membership) {
    overrides.Membership = {
      membershipContainer: `
        background-color: #F6F5F2;
        margin:0;
      `,
      wrapper: `
      ${themeStyles.Membership?.wrapper ?? ''}
      padding-top: 15px;
      flex-direction: column;
      background-color: #F6F5F2;

      ${vars.breakpoints.md} {
        flex-direction: row;
      }

      .banner-content_container{
        & h2{
        font-family: ${vars.fonts.familyTertiary};
            font-size: ${vars.fonts.fontSize26};
            font-weight: ${vars.fonts.weightNormal};
            text-transform: unset;
        }
      }

    .card-container {
      .card-layout-wrapper {
        
        display: grid;
        grid-template-columns: 1fr;
        grid-gap: 10px;

        ${vars.breakpoints.sm} {
          grid-template-columns: repeat(3, 1fr); 
        }

        .membership-card {
            width: auto;
            height: auto;

          .cancel-membership {
            width: 100%;

            .cancel-membership-text {
              width: 100%;
            }
          }

          .change-payment-card a {
            text-decoration: underline;

            :hover {
              text-decoration: none;
            }
          }
        }

        .faq-card {
          grid-column: 1/-1;

          ${vars.breakpoints.sm} {
            grid-column: 1/3;
            &.active{
              grid-column: 2/4;
            }
          }
        }
        
      }
    }

      .mobile-sidebar-view {
        display: flex;
        flex-direction: column;
        padding:0px;

        > .membership-sidebar {
          width: 100%;
          background: ${vars.colors.grey10};
          height: fit-content;
          padding-left: 20px;
          margin-bottom: 10px;
        }

        ${vars.breakpoints.md} {
          display: none;
        }


        > .sidebar-dropdown-text {
            display: block;
            width: 100%;
            border-color: ${vars.colors.grey20};
            color: ${vars.colors.secondary8};
            height: 40px;
            cursor: pointer;
            border: 1px solid transparent;
            border-radius: 5px;
            font-size: 14px;
            font-weight: bold;
        }
      }

      .hide-sidebar {
        display: none;
      }

      .sidebar-view {
        > .membership-sidebar {
          background: ${vars.colors.grey10};
          height: fit-content;
          margin-bottom: 10px;
          max-width: 230px;
          margin-right: 15px;
          padding-left: 12px;
        }
      }

      .sidebar {
        background-color: #F6F5F2;
      > .membership-sidebar {
        width: 100%;
        background: #FFFFFF;
        height: fit-content;
        padding-left: 20px;
        margin-bottom: 10px;
        
        ${vars.breakpoints.md} {
          max-width: 230px;
          margin-right: 15px;
          padding-left: 12px;
        }

        > img {
          width: 14px;
          height: 14px;
        }
        & .navigation-link {
            max-height: fit-content;
            > h4 {
              text-transform: uppercase;
              font-size: 16px;
              color: #333333;
              margin-top: 12px;
              margin-bottom: 15px;
              text-wrap: nowrap;
              width: fit-content;
              font-weight: bold;
              
            }
            & a{
              font-size:14px;
              color: #333333;
              margin-left: 4px;
              margin-bottom: 10px;
              text-wrap: nowrap;
              height: 24px;
              width: fit-content;
              display: flex;
              gap: 10px;
              > div {
                overflow-y: hidden;
                > img {
                  width: 24px;
                  height: 24px;
                }
              }
            }
        }
      }
    }`,
    };
  }

  if (themeStyles.GlobalHeader) {
    overrides.GlobalHeader = {
      mainNavBarWrapper: `
        ${themeStyles.GlobalHeader?.mainNavBarWrapper ?? ''}
        .megaNavTab {
          font-size: 18px;
          text-underline-offset: ${vars.space.lg};
          padding: ${vars.space.lg} 15px;
          display: inline-block;
          font-family: ${vars.fonts.headerMainNavTitle};
          color: ${vars.colors.grey50};
          font-weight: 500;
          letter-spacing: ${vars.fonts.fontLetterSpacing5};
          ${vars.breakpoints.xl} {
            padding: ${vars.space.lg} 20px;
          }

          &:hover{
            text-decoation: none;
          }

          &.emphasize, &.emphasize span {
            color: ${vars.colors.primary1};
            font-weight: ${vars.fonts.weightBold};
          }
          &:focus{
            text-decoration: none;
          }
        }
      `,
    };
  }

  if (themeStyles.Cart && (themeStyles.Cart.MiniCart || themeStyles.Cart.cartDetailSection || themeStyles.Cart.Pill)) {
    overrides.Cart = {
      MiniCart: `
      ${themeStyles.Cart?.MiniCart ?? ''}

      .price-section {
        ${vars.breakpoints.md}{ 
          flex-flow: column;
          align-items: baseline;
        }
      }

      .price-section .strike,  .price-section .sale-price {
        ${vars.breakpoints.md}{ 
          font-size: 24px;
          line-height: 27.6px;
        }
      }

      .price-section .strike+.sale-price {
        color: ${vars.colors.primary1}
      }

      .gift-other-device h4, .gift-tablet h4 {
        color: ${vars.colors.secondary8};
      } 

      .auc-Recommend .mini-cart-title, .product-item-container .product-title,  .product-price span.price-cart {
        font-size:20px;
        line-height:24px;
        font-weight: ${vars.fonts.weightMedium};
        font-family: ${vars.fonts.familyPrimary};
        margin:18px 0;
        letter-spacing: -0.02em;
        ${vars.breakpoints.md} {
          font-size:24px;
          line-height:28.8px;
        }
      }

      .auc-Recommend .mini-cart-title, .product-item-container .product-title {
       color: ${vars.colors.secondary8};
      }

      .product-item-container .product-title {
        height:auto;
      }

      .review-count {
        font-size:24px;
        line-height:28.8px;
      }

      .continue-shopping-text {
        color: ${vars.colors.secondary8};
        font-family: ${vars.fonts.familySecondary};
        font-size: 12px;
        font-weight: ${vars.fonts.weightMediumPlus};
        line-height: 14px;
        letter-spacing: 0.02em;
      }
    `,
      cartDetailSection: `
      ${themeStyles.Cart?.cartDetailSection ?? ''}
      .product-link {
        color: ${vars.colors.secondary8};
      }`,
      Pill: `
        ${themeStyles.Cart?.Pill ?? ''}
        background-color: ${vars.colors.bgScarcityColor};
      `,
    };
  }

  if (themeStyles.Product && (themeStyles.Product.MemberPricing || themeStyles.Product.MemberPricingPopup)) {
    overrides.Product = {
      MemberPricing: `
      ${themeStyles.Product?.MemberPricing ?? ''}
      .unlimited-icon i, .member-price-content, button.popup-button {
        color: ${vars.colors.black};
      }
      &.list-view {
        + .price-per-litre, .price-per-litre {
          color: ${vars.colors.black} !important;
        }
      }
      .unlimited-icon i {
        padding: 5px;
      }
      `,
      MemberPricingPopup: `
      ${themeStyles.Product?.MemberPricingPopup ?? ''}
      .mantine-Modal-body  .header-container .unlimited-icon i {
        color: ${vars.colors.black};
      }
      `,
    };
  }

  if (themeStyles.MyAccount) {
    overrides.MyAccount = {
      flexFooterModalWrapper: {
        wrapper: `
          ${themeStyles.MyAccount?.flexFooterModalWrapper?.wrapper ?? ''}
          .discount {
            color: ${vars.colors.primary1} !important;
          }
        `,
      },
      flexMobileFooterModalWrapper: {
        wrapper: `
          ${themeStyles.MyAccount?.flexMobileFooterModalWrapper?.wrapper ?? ''}
          .discount {
            color: ${vars.colors.primary1} !important;
          }
        `,
      },
      cancelWineplanSubscriptionModalWrapper: `
        ${themeStyles.MyAccount?.cancelWineplanSubscriptionModalWrapper ?? ''}
        
        .coupon-container{
          height: 179px;
          width: fit-content;
          gap:32px;
          padding: 32px;
          overflow: visible;
          ${vars.breakpoints.sm} {
            gap: 16px;
            height: 68px;
            width: 664px;
            padding: 0;
          } 
        }
        
        .coupon{
          position:relative;
          background-color: #004D86;
          height:68px;
          width:193px;
          border-radius: 4px;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: visible;
          color: #ffffff;
          
          i {
            color: #ffffff;
          }
          
          .mantine-Text-root{
            font-family: Oswald;
            font-weight: ${vars.fonts.weightMedium};
            letter-spacing: -0.04em;
            line-height: 105%;
            font-size:30px;
            color: #ffffff;
          }
        }

        .coupon::before,
        .coupon::after{
          content: '';
          position: absolute;
          width: 20.53px;
          height: 20px;
          top: 50%;
          background-color: #ffffff;
          transform: translateY(-50%);
          border-radius: 50%;
        }
        
        .coupon::before{
          left: -10.55px;
        }
        
        .coupon::after{
          left: 182.72px;
        }

        .coupon-notes{
          font-weight: ${vars.fonts.weightBold};
          font-size: 30px;
          font-family: Oswald;
          letter-spacing: -0.02em;
          line-height:110.00000000000001%;
          color: ${vars.colors.secondary8};
        }
        
        button.unlimited-modal-primary-cta {
          background-color: ${vars.colors.btnsPrimaryBg} !important;
          
          &:hover {
            background-color: ${vars.colors.btnsPrimaryHoverBg} !important;
          }
        }
        
        button.wineplan-modal-primary-cta {
          background-color: ${vars.colors.btnsPrimaryBg} !important;
          color: ${vars.colors.btnsPrimaryColor} !important;
          
          &:hover {
            background-color: ${vars.colors.btnsPrimaryHoverBg} !important;
            color: ${vars.colors.btnsPrimaryHoverColor} !important;
          }
        }
        
        .wineplan-modal-content-wrapper button.wineplan-modal-primary-cta {
          background-color: ${vars.colors.btnsPrimaryBg} !important;
          color: ${vars.colors.btnsPrimaryColor} !important;
          
          &:hover {
            background-color: ${vars.colors.btnsPrimaryHoverBg} !important;
            color: ${vars.colors.btnsPrimaryHoverColor} !important;
          }
        }
      `,
      pushbackSaveOfferModalWrapper: {
        wrapper: `
          ${themeStyles.MyAccount?.pushbackSaveOfferModalWrapper?.wrapper ?? ''}
          
          .offer-batch {
            padding: 10px 30px;
            background-size: contain;
            background-position: center;
            background-repeat: no-repeat;
            height: 68px;
            background-image: url("/next/images/cancel-save-offer-bg.png");
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 10px;
            color: white;
            text-align: center;
            
            i {
              color: #ffffff;
            }
            
            .offer-batch-text {
              font-family: PT Sans !important;
              font-size: ${vars.fonts.fontSize24};
              ${vars.breakpoints.xl} {
                font-size: ${vars.fonts.fontSize30};
              }
              font-weight: ${vars.fonts.weightMedium};
              color: #ffffff;
            }
          }
          
          .offer-right-content {
            color: ${vars.colors.secondary8};
            font-family: PT Sans !important;
            font-size: ${vars.fonts.fontSize24};
            ${vars.breakpoints.xl} {
              font-size: ${vars.fonts.fontSize30};
            }
            font-weight: ${vars.fonts.weightMedium};
          }
          
          button.pushback-save-modal-primary-cta {
            background-color: ${vars.colors.btnsPrimaryBg} !important;
            
            &:hover {
              background-color: ${vars.colors.btnsPrimaryHoverBg} !important;
            }
          }
        `,
      },
      winePlanProductImage: `
        width: 100%;
        .product-image-container {
        position: relative;
        padding: 16px 0;
        margin-bottom: 13px;
        background-color: ${(vars.colors.bgColors as unknown as { [key: string]: string }).STWCCream}; 
        .label-card {
          display: none;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          background: rgb(248, 245, 239);
          color: black;
          padding: 10px;
          box-sizing: border-box;
          z-index: 1;
          height: 180px;
          flex-direction: column;
          justify-content: center;
          align-items: start;
        
          ${vars.breakpoints.lg} {
            height: 220px;
          }
        
          .label-row {
            margin-left: 20%;
          
            ${vars.breakpoints.lg} {
              margin-left: 0;
            }
          
            i {
              padding: 5px;
            }
          
            display: grid;
            grid-template-columns: auto 1fr;
            align-items: center;
            padding:8px;
          }
        }
      
        &:hover .label-card {
          display: flex;
        }
      
        .image-container {
          display: flex;
          justify-content: center;
          background-color: ${(vars.colors.bgColors as unknown as { [key: string]: string }).STWCCream};
        
          .image-size {
            position: relative;
            width: 48px;
            height: 180px;
          
            ${vars.breakpoints.lg} {
              width: 120px;
              height: 220px;
            }
          }
        
          &.gift-image {
            .image-size {
              width: 220px;
            }
          }
        
        }
      }
    `,
      winePlanModalWrapper: `
      ${themeStyles.MyAccount?.winePlanModalWrapper ?? ''}
      
      button.unlimited-modal-primary-cta {
        background-color: ${vars.colors.btnsPrimaryBg} !important;
        
        &:hover {
          background-color: ${vars.colors.btnsPrimaryHoverBg} !important;
        }
      }
      
      button.wineplan-modal-primary-cta {
        background-color: ${vars.colors.btnsPrimaryBg} !important;
        
        &:hover {
          background-color: ${vars.colors.btnsPrimaryHoverBg} !important;
        }
      }
      
      .wineplan-modal-content-wrapper button.wineplan-modal-primary-cta {
        background-color: ${vars.colors.btnsPrimaryBg} !important;
        color: ${vars.colors.btnsPrimaryColor} !important;
        
        &:hover {
          background-color: ${vars.colors.btnsPrimaryHoverBg} !important;
          color: ${vars.colors.btnsPrimaryHoverColor} !important;
        }
      }
      
      .submit-button {
        background: ${vars.colors.btnsPrimaryBg} !important;
        
        &:hover {
          background-color: ${vars.colors.btnsPrimaryHoverBg} !important;
        }
      }
    `,
      winePlan: `
        display: grid;

        .error-message {
          margin-top:16px;
          padding:18px;
          background-color:${vars.colors.errorLight};
          color:${vars.colors.error};
        }
    
        .delay-delivery-button {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-bottom: 30px;
          button.cancel-tertiary-cta {
            margin: 0;
          }
        }

        button.cancel-tertiary-cta {
          cursor: pointer;
          padding: 0px;
          text-decoration: underline;
          text-underline-offset: 4px;
          border:none;
          font-weight: ${vars.fonts.weightNormal};
          font-size: ${vars.fonts.sizeDefault};
          text-align: left;
          background-color: transparent;
          color: ${vars.colors.black};
          &:hover {
            text-decoration: underline;
            color: ${vars.colors.black};
            background-color: transparent;
            border: none;
          }
          width: fit-content;
          margin-bottom: 20px;
          ${vars.breakpoints.sm} {
            width: fit-content;
            margin-bottom: 30px;
          }
        }

          .spinner {
            min-height: 50px;
            position: relative;
            margin-top: 50px;
          }
          .payment-method {
              margin: 0;
              &:last-of-type{
                margin-bottom: 40px;
                .mantine-Accordion-item::after {
                  content: "";
                  left: -16px;
                  right: -16px;
                  height: 1px;
                  background-color: ${vars.colors.secondary9};
                  position: absolute;
                  ${vars.breakpoints.sm} {
                    left: 0px;
                    right: 0px;
                  }
                }
              }
          }
          .subscription-option{         
            h6{
              font-size:26px;
              font-family: ${vars.fonts.familyTertiary};
            }
            .loadIcon {
              margin: 10px 0;
            }
          }
          .case-details{
            display: grid;
            margin-top:${vars.space.xl2};
            gap:${vars.space.xl};
            ${vars.breakpoints.xs} {
              grid-template-columns: 1fr;
            }
            ${vars.breakpoints.sm} {
            grid-template-columns:0.8fr 1fr;
            }

              .case-image{
                display: grid;
                justify-content:end;
                position:relative;
                width:90px;
                height:82px;
                justify-self: flex-end;
                @media (min-width: 360px) {
                  display:none;
                }
                ${vars.breakpoints.sm} {
                  display: grid;
                  width:164px;
                  height:146px;
                }
                ${vars.breakpoints.md} {
                  display: grid;
                  width:146px;
                  height:130px;
                }
                ${vars.breakpoints.lg} {
                  display: grid;
                  width:174px;
                  height:156px;
                }
                ${vars.breakpoints.xl} {
                  display: grid;
                  width:190px;
                  height:170px;
                }
                ${vars.breakpoints.xxl} {
                  display: grid;
                  width:260px;
                  height:233px;
                }
              }
              .personalized-section {
                    margin: 8px 0 0;
                    ${vars.mixins.body3}
                    font-weight: ${vars.fonts.weightMediumPlus};
                    color:#006E1A;
                    background:  ${vars.colors.secondary16};
                    padding: 4px 12px;
                    border-radius: 12px;
              }
              .case-info{
              display: flex;
              flex-direction: column;
              padding-top: 1.15rem;
              align-items: start;
                .scheme-name{
                  font-size:24px;
                  font-weight: ${vars.fonts.weightMedium};
                  ${vars.breakpoints.sm} {
                    font-size:30px;

                    }
                  ${vars.breakpoints.xxl} {
                    font-size:34px;
                    text-transform: uppercase;
                  }


                  font-family: ${vars.fonts.familyTertiary};
                  line-height: 35.7px;


                }
                .case-price-info{
                  font-size: 18px;
                  font-weight: ${vars.fonts.weightBold};
                  ${vars.breakpoints.sm} {
                    font-size: 22px;
                    font-weight: ${vars.fonts.weightMediumPlus};
                  }

                  display:flex;
                  flex-direction: column;
                  ${vars.breakpoints.sm} {
                  align-items:center;
                  flex-direction: row;
                  margin-top:${vars.space.xs};
                  }
                  padding:10px 0px;
                  .case-price{
                  line-height: 26.4px;
                  }
                  .case-saving-price{
                    margin-top:${vars.space.xs};
                    ${vars.breakpoints.sm} {
                      margin-top:0;
                      margin-left:${vars.space.xs};
                      }

                    color: ${vars.colors.primary1};
                    background: ${vars.colors.primary5};
                    border-radius:16px;
                    padding:4px 10px;
                    width: fit-content;
                    font-size: 12px;

                  }
                    .case-discount-price{
                    margin-top:${vars.space.xs};
                    ${vars.breakpoints.sm} {
                      margin-top:0;
                      margin-left:${vars.space.xs};
                      }

                    color: ${vars.colors.secondary6};
                    background:  ${vars.colors.secondary16};
                    border-radius:16px;
                    padding:8px;
                    width: fit-content;
                    font-size: 12px;

                  }
                }
                .case-standard-price{
                  color: ${vars.colors.grey60};
                  padding-bottom:13px;
                  font-size: 16px;

                }
                .case-bottle-count{
                  font-size: 16px;
                  color: ${vars.colors.grey50};
                  font-weight: ${vars.fonts.weightNormal};

                }
                .case-swap-links {
                  display: flex;
                  margin-top: 8px;
                  gap: 4px;
                  flex-direction: column;
                  ${vars.breakpoints.xs} {
                    width: 100%;
                  }
                  ${vars.breakpoints.sm} {
                    flex-direction: row;
                    width: fit-content;
                  }
                  ${vars.breakpoints.md} {
                    width: fit-content;
                  }
                  ${vars.breakpoints.xl} {
                    width: fit-content;
                  }
                  ${vars.breakpoints.xxl} {
                    width: fit-content;
                  }
                }
                .case-not-select-container{
                  display: flex;
              flex-direction: column;
              align-items: start;
              .coming-soon-heading {
                color: ${vars.colors.grey50};
                margin: 0;
                padding:13px 0px;
              }
              .case-not-select-notes{
                font-size: ${vars.fonts.sizeH4};

              }
              .take-case{
                width:190px;
                margin:13px 0px;
                font-size: 14px;
                font-weight: ${vars.fonts.weightMediumPlus};
                border-radius: 4px;
              }
                }
              }

          }
          .bottles-details-wrapper{
          display:grid;
          margin-top: ${vars.space.xl};
            .header{
              font-size: 18px;
              font-weight: ${vars.fonts.weightMediumPlus};
              font-family: ${vars.fonts.familyTertiary};

            }
          .case-update {
            ${vars.mixins.body2}
            margin-top: 12px;
            display: flex;
            gap: 8px;
            align-items: center;
            color:#006E1A;
            & i {
              font-size: 18px;
            }
          }
          .product-row{
            display:flex;
            align-items: center;
            .arrow-slide {
              background:white;
              border-radius:15px;
              cursor: pointer;
              ${vars.breakpoints.xs} {
              position: absolute;
              z-index: 1;
              &.right{
                right:25px;
                }
              }
              ${vars.breakpoints.sm}{
                position: relative;
                &.right{
                margin:auto;
                margin-right: 0px;
                }
              }
            }

            .bottles-details {

              display: grid;

              .bottle-container {
                display: grid;
                grid-auto-flow: column; /* Arrange items in a single row */
                grid-auto-columns: minmax(293.33px, 1fr); /* Define minimum and maximum column sizes */
                overflow-x: auto; /* Enable horizontal scrolling */
                white-space: nowrap; /* Prevent line breaks */
                gap: 10px; /*space between grid items */
                padding: 20px;
                scrollbar-width: none;


                  .product-card {
                    display: flex;
                    position: relative;
                    flex-direction: column;
                    align-items: center;
                    padding: 15px;
                    white-space: normal;
                    height:100%;
                      &.gift-card{
                        border: 1px solid ${vars.colors.secondary9};
                        border-radius: 4px;
                        background: none;
                          & .gift-tag{
                            justify-content: start;
                            position: absolute;
                            display:flex;
                            gap:7px;
                            background-color: ${vars.colors.grey00};
                            padding:10px;
                            border-radius: 4px;
                            font-weight:700;
                            font-size:16px;
                            z-index:1;
                          }
                        }
                    & .product-details-parent{
                       height:auto;
                       ${vars.breakpoints.sm}{
                         height:100%;
                       }
                    }
                    .product-details{
                      margin: auto;
                      margin-bottom: 0;
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      height:100%;
                      ${vars.breakpoints.sm} {
                         align-items: start;
                      }
                      .product-name{
                      font-size: 18px;
                      font-weight: ${vars.fonts.weightMediumPlus};
                      margin: 18px 0 15px 0;
                      font-family: ${vars.fonts.familyTertiary};
                      margin-top: 10px;
                      &.single-line{
                        max-height: 4em;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                      }
                      }
                      .product-count{
                        color: ${vars.colors.grey60};
                        font-size: 16px;
                        font-weight: ${vars.fonts.weightNormal};
                        padding-bottom: 15px;
                        align-items: center;
                        display: flex;
                        margin-top:auto;
                      }
                    
                      .product-label,.mobile-product-label{
                        display:none;
                        ${vars.breakpoints.sm} {
                          display:block;
                          cursor:pointer;
                          }
                        background:none;
                        border:none;
                        color: ${vars.colors.grey50};
                        font-size: 16px;
                        font-weight: ${vars.fonts.weightNormal};
                        padding-top: 10px;
                        padding-bottom: 2px;
                        width: fit-content;
                        box-shadow: inset 0 -1px 0 0 black;
                        cursor:pointer;

                      }
                       .mobile-product-label{
                         display:block;
                        ${vars.breakpoints.sm} {
                          display:none;
                          cursor:pointer;
                          }
                      }
                      &.non-wine-gift{
                        .product-label,.product-count,.wine-rating{
                          visibility: hidden;
                        }

                      }
                    }
                    .product-name{
                        font-size: 18px;
                        font-weight: ${vars.fonts.weightMediumPlus};
                        font-family: ${vars.fonts.familyTertiary};
                        padding: 16px;
                    }
                    .image-container {
                      display: flex;
                      justify-content: center;
                    
                      .image-size {
                        position: relative;
                        width: 48px;
                        height: 180px;
                      
                        ${vars.breakpoints.lg} {
                          width: 120px;
                          height: 220px;
                        }
                    }
                }
              }
            }
          }
          }
      `,
      productHitWrapper: {
        wrapper: `
          ${themeStyles.MyAccount?.productHitWrapper?.wrapper ?? ''}
          &.hit-product {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
          }
          .image-bg {
            background: ${vars.colors.primary3};
          }
          .image-container {
            width: 100%;
            height: 290px;
            position: relative;
          }
          .image img {
            padding: 16px;
          }
          .hit-content {
            display: flex;
            flex-direction: column;
            gap: 8px;
            flex: 1;
            padding: 8px 16px 0;
            ${vars.breakpoints.sm} {
              padding: 0;
              gap: 24px;
            }
          }
          .title, .image-bg {
            cursor: pointer;
          }
          .case-update {
            ${vars.mixins.body3}
            font-weight: ${vars.fonts.weightMediumPlus};
            color:#006E1A;
            background:  ${vars.colors.secondary16};
            padding: 4px;
            border-radius: 12px;
            width: max-content;
            display: flex;
            gap: 8px;
            align-items: center;
            position: absolute;
            top: 1rem;
            left: 0.5rem;
            ${vars.breakpoints.sm} {
              padding: 4px 12px;
            }
            span {
              display: none;
              ${vars.breakpoints.sm} {
                display: inline;
              }
            }
          }
          .title-container {
            padding-top: 0;
            display: flex;
            flex-direction: column;
            gap: 4px;
            ${vars.breakpoints.sm} {
              padding-top: 12px;
            }
            .title {
              ${vars.mixins.body1}
              text-align: left;
              font-size: ${vars.fonts.fontSize16};
              font-weight: ${vars.fonts.weightNormal};
              line-height: 1.4;
            }
            .product-description {
              ${vars.mixins.body2}
              color: ${vars.colors.secondary15};
            }
            .product-description-content {
              gap: 6px;
              align-items: center;
              flex-flow: wrap;
              display: none;
              ${vars.breakpoints.sm} {
                display: flex;
              }
              span.seperator {
                width: 4px;
                height: 4px;
                background: ${vars.colors.secondary9};
                border-radius: 4px;
              }
            }
          }
          .info-container {
            display: flex;
            flex-direction: column;
            gap: 12px;
            .rating {
              display: flex;
              gap: 4px;
              align-items: center;
            }
            .reviews {
              ${vars.mixins.body1}
              position: relative;
              top: -2px;
            }
            .addToCase-container button{
              cursor: pointer;
              text-decoration: none;
              padding: 7px 16px;
              text-transform: uppercase;
              border: 1px solid ${vars.colors.primary8};
              border-radius: 4px;
              ${vars.mixins.body1}
              font-weight: ${vars.fonts.weightMediumPlus};
              font-size: ${vars.fonts.sizeSmall};
              line-height: 14px;
              text-align:center;
              color: initial;
              &:hover {
                background-color: ${vars.colors.btnsPrimaryHoverBg};
                color: ${vars.colors.btnsPrimaryHoverColor};
              }
              ${vars.breakpoints.sm} {
                font-size: ${vars.fonts.fontSize16};
                line-height: 16px;
                padding: 12px 24px;
              }
            }
            .product-quantity {
              display: flex;
              align-items: center;
              gap: 8px;
              .cart-count {
                ${vars.mixins.body1}
                padding: 0 4px;
              }
              button {
                border-radius: 16px;
                padding: 0;
                width: 32px;
                height: 32px;
                border-color: ${vars.colors.secondary9}
              }
              button:focus {
                  padding: 0;
                  border-color: ${vars.colors.secondary9};
                  border-width: 1px;
              }
              button:hover {
                  background-color: #fff;
                  color: initial;
              }
              i {
                font-size: 16px;
              }
            }
          }
          .price-container {
            display: flex;
            gap: 4px;
            flex-direction: column;
            ${vars.mixins.body2}
            ${vars.breakpoints.sm} {
              flex-direction: row;
            }
            .price-value-text {
              display: flex;
              gap: 4px;
            }
            .price-value-discounted {
              font-weight: ${vars.fonts.weightBold};
            }
            .price-value-actual-strike {
              text-decoration: line-through;
            }
          }
        `,
      },
      yourFutureDelivery: `
      ${themeStyles.MyAccount?.yourFutureDelivery ?? ''}
      .delivery-heading {
          font-family: ${vars.fonts.familyTertiary};
        }
      `,
    };
  }

  return merge({}, themeStyles, overrides) as ReturnType<typeof buildThemeWidgetStylesLW>;
};

export const buildThemeWidgetStylesSTWC = (vars: Omit<Theme, 'widgets'>): ReturnType<typeof buildThemeWidgetStylesLW> =>
  buildThemeWidgetStylesSTWCFromBase(vars, buildThemeWidgetStylesLW(vars));

// CMS pages: core + product cards + minicart (header)
export const createCmsPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsSTWC, buildThemeMixinsSTWC, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesSTWCFromBase(vars, {
      ...buildCoreWidgets(vars),
      ...buildProductCardWidgets(vars),
      ...buildMiniCartWidgets(vars),
    }),
  );

// PDP: core + full product + minicart (header)
export const createProductTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsSTWC, buildThemeMixinsSTWC, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesSTWCFromBase(vars, {
      ...buildCoreWidgets(vars),
      ...buildProductWidgets(vars),
      ...buildMiniCartWidgets(vars),
    }),
  );

// Search/PLP: core + search + product cards + minicart
export const createSearchTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsSTWC, buildThemeMixinsSTWC, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesSTWCFromBase(vars, {
      ...buildCoreWidgets(vars),
      ...buildSearchWidgets(vars),
      ...buildProductWidgets(vars),
      ...buildMiniCartWidgets(vars),
    }),
  );

// Cart: core + product cards + cart
export const createCartTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsSTWC, buildThemeMixinsSTWC, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesSTWCFromBase(vars, {
      ...buildCoreWidgets(vars),
      ...buildProductCardWidgets(vars),
      ...buildCartWidgets(vars),
    }),
  );

// Checkout: core + full cart widgets (for PresellBadge/ReservationItem modal styles and other cart components)
export const createCheckoutTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsSTWC, buildThemeMixinsSTWC, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesSTWCFromBase(vars, {
      ...buildCoreWidgets(vars),
      ...buildCartWidgets(vars),
    }),
  );

// Account: core + account + minicart (header)
export const createAccountTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsSTWC, buildThemeMixinsSTWC, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesSTWCFromBase(vars, {
      ...buildCoreWidgets(vars),
      ...buildAccountWidgets(vars),
      ...buildMiniCartWidgets(vars),
    }),
  );

const createTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsSTWC, buildThemeMixinsSTWC, buildThemeWidgetStylesSTWC);
export default createTheme;
